/**
 * plugin-guard.ts — Loopback guard for ɳSentry plugin calls
 *
 * Purpose:    A public page must never probe the visitor's own machine. Plugin
 *             calls default to http://localhost:<port> (self-host), so
 *             pluginFetch refuses a loopback target unless the page itself is
 *             served from a loopback host (D-0898; the P7-PROD-25 CSP keeps
 *             loopback out of connect-src on purpose).
 * Inputs:     A target URL and the page hostname (location.hostname)
 * Outputs:    Booleans, and the typed SentryError returned on a refusal
 * Constraints: Pure, no I/O. Fails closed on an unparsable URL. Judges the URL's own host only: a public DNS name
 *             that resolves to 127.0.0.1 is not detectable here (CSP covers it).
 * SOT:        P7-PROD-85 / D-0898
 */

import { ERROR_MESSAGES, type SentryError } from './sentry-error'

const IPV4_LOOPBACK = /^127(?:\.\d{1,3}){3}$/
// IPv6 forms of 127.0.0.0/8 after URL normalisation: IPv4-mapped [::ffff:7f00:1],
// IPv4-compatible [::7f00:1] and NAT64 [64:ff9b::7f00:1]
const EMBEDDED_LOOPBACK = /^(?:::ffff:|::|64:ff9b::)7f[0-9a-f]{2}:[0-9a-f]{1,4}$/

/** True for localhost, *.localhost, 127.0.0.0/8, 0.0.0.0 and the IPv6 loopback forms (::1, ::, embedded 127/8). */
export function isLoopbackHost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase().replace(/^\[|\]$/g, '').replace(/\.+$/, '')
  return (
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host === '0.0.0.0' ||
    host === '::1' ||
    host === '::' ||
    host === '::ffff:0:0' ||
    IPV4_LOOPBACK.test(host) ||
    EMBEDDED_LOOPBACK.test(host)
  )
}

/**
 * True when the URL points at a loopback host. Resolved against a public
 * placeholder origin the way fetch resolves it, so a protocol-relative base
 * ('//localhost:3831/x') is judged by its real host and a plain relative path is
 * not loopback. A URL that cannot be parsed at all fails closed (true): fetch
 * would throw on it too, so refusing costs nothing.
 */
export function isLoopbackTarget(url: string): boolean {
  try {
    return isLoopbackHost(new URL(url, 'https://page.invalid/').hostname)
  } catch {
    return true
  }
}

/** True when the page itself is served from a loopback host (self-host or dev). */
export function pageIsLoopback(pageHostname: string): boolean {
  return isLoopbackHost(pageHostname)
}

/** The call must be refused: loopback target requested from a non-loopback page. */
export function shouldRefuseTarget(url: string, pageHostname: string): boolean {
  return isLoopbackTarget(url) && !pageIsLoopback(pageHostname)
}

/** The typed error pluginFetch returns on a refusal (same shape as an unreachable plugin). */
export function loopbackRefusedError(): SentryError {
  return { type: 'plugin_offline', message: ERROR_MESSAGES.plugin_offline }
}
